import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import TeamIntegrationItem from './TeamIntegrationItem';
import { getIconComponent } from 'utils/icons/iconUtils';
import { PowerOutlinedIcon } from 'icons/materialIcons';

const TeamLeadExportContainer = ({
    searchTerm = '',
    subteamId = null,
    availableIntegrations = [],
    onIntegrationClick,
    isIntegrationConnected,
    getConnectionStatus
}) => {
    // Lead Export integrations: Salesforce, HubSpot, Zoho, Microsoft Dynamics, Pardot, Pipedrive, Monday, Marketo, Zapier, Slack
    const integrationNames = [
        'Salesforce',
        'HubSpot',
        'Zoho',
        'Microsoft Dynamics',
        'Pardot',
        'Pipedrive',
        'Monday',
        'Marketo',
        'Zapier',
        'Slack'
    ];

    // Match with available integrations from backend, or use fallback
    const leadExportIntegrations = integrationNames
        .map(name => {
            // Try to find in availableIntegrations (case-insensitive matching)
            const backendIntegration = availableIntegrations.find(
                i => i.name?.toLowerCase() === name.toLowerCase() ||
                    i.displayName?.toLowerCase() === name.toLowerCase() ||
                    (i.name === 'zoho' && name.toLowerCase() === 'zoho') ||
                    (i.name === 'zoho crm' && name.toLowerCase() === 'zoho')
            );

            if (backendIntegration) {
                return {
                    name: backendIntegration.displayName || backendIntegration.name,
                    _id: backendIntegration._id,
                    connectionType: backendIntegration.connectionType,
                    isConnected: isIntegrationConnected ? isIntegrationConnected(name) : false,
                    connectionStatus: getConnectionStatus ? getConnectionStatus(name) : 'disconnected'
                };
            }

            // Fallback if not found in backend
            return {
                name: name,
                _id: null,
                connectionType: null,
                isConnected: false,
                connectionStatus: 'disconnected'
            };
        })
        .filter(integration =>
            integration.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // Only show the "Don't see your integration" option when not searching or when the search matches it
    const showRequestOption = !searchTerm || 'don\'t see your integration'.includes(searchTerm.toLowerCase());

    if (leadExportIntegrations.length === 0 && !showRequestOption && searchTerm) {
        return null;
    }

    return (
        <Box>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#242020',
                    fontSize: '2rem'
                }}
            >
                Lead Export
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    mb: 3,
                    color: '#828282'
                }}
            >
                Export Touch Leads to your CRM, Marketing Engagement tool and more
            </Typography>
            <Grid container spacing={2}>
                {leadExportIntegrations.map((integration) => {
                    const isZoho = integration.name === 'Zoho' || integration.name === 'Zoho CRM';
                    const IconComponent = getIconComponent(integration.name);
                    const isConnected = integration.isConnected;

                    if (isZoho) {
                        return (
                            <Grid item xs={12} sm={6} md={3} key={integration.name}>
                                <Box
                                    onClick={() => onIntegrationClick && onIntegrationClick(integration.name)}
                                    sx={{
                                        width: '100%',
                                        border: '1px solid #00000014',
                                        cursor: 'pointer',
                                        minHeight: '72px',
                                        position: 'relative',
                                        borderRadius: '12px',
                                        backgroundColor: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px 24px',
                                        '&:hover': {
                                            backgroundColor: '#f7f7f7'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '48px',
                                            border: '1px solid #00000014',
                                            height: '48px',
                                            display: 'flex',
                                            minWidth: '48px',
                                            minHeight: '48px',
                                            alignItems: 'center',
                                            borderRadius: '8px',
                                            justifyContent: 'center',
                                            backgroundColor: '#ffffff',
                                            marginRight: '12px',
                                            alignSelf: 'center'
                                        }}
                                    >
                                        {IconComponent && (typeof IconComponent === 'function' ? <IconComponent width={32} height={32} /> : IconComponent)}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                mb: 0.5
                                            }}
                                        >
                                            {integration.name === 'Zoho CRM' ? 'Zoho' : integration.name}
                                        </Typography>
                                        <Chip
                                            label="RECOMMENDED"
                                            size="small"
                                            sx={{
                                                alignSelf: 'flex-start',
                                                backgroundColor: '#29AEF8',
                                                color: '#ffffff',
                                                fontSize: '8px',
                                                fontWeight: 700,
                                                height: '18px',
                                                '& .MuiChip-label': {
                                                    padding: '0 6px',
                                                    fontSize: '8px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    }

                    return (
                        <Grid item xs={12} sm={6} md={3} key={integration.name}>
                            <TeamIntegrationItem
                                text={integration.name}
                                icon={IconComponent}
                                onClick={() => onIntegrationClick && onIntegrationClick(integration.name)}
                                isConnected={isConnected}
                                connectionStatus={integration.connectionStatus}
                            />
                        </Grid>
                    );
                })}

                {/* Request integration option */}
                {showRequestOption && (
                    <Grid item xs={12} sm={6} md={3}>
                        <TeamIntegrationItem
                            text={
                                <Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                        Don't see your integration?
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#818181' }}>
                                        Request one here
                                    </Typography>
                                </Box>
                            }
                            icon={<PowerOutlinedIcon sx={{ color: '#29AEF8' }} />}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TeamLeadExportContainer;

